import { CourseBuilder, Frame, SURFACE } from './builder.js';
import type { Course, Decor, Material, V3 } from './builder.js';
import {
  box,
  flatRail,
  ground,
  hubba,
  post,
  quarterPipe,
  railWithPosts,
  stairs,
  sunkenBowl,
  tube,
  wedge,
} from './primitives.js';
import type { Rect } from './primitives.js';
import { COURSE_HALF_EXTENT, COURSE_SPAWNS } from './spawns.js';

/** Frame yaw that makes a ramp built along local -z rise toward the given world direction. */
const RISE = {
  north: 0,
  south: Math.PI,
  east: -Math.PI / 2,
  west: Math.PI / 2,
} as const;

const PLOTS = {
  mainPool: { x0: -50, x1: 0, z0: -130, z1: -90 },
  roundBowl: { x0: 10, x1: 40, z0: -120, z1: -100 },
  canal: { x0: -130, x1: 130, z0: 100, z1: 120 },
  cornerBowl: { x0: 140, x1: 170, z0: 130, z1: 150 },
} satisfies Record<string, Rect>;

function inside(x: number, z: number, x0: number, x1: number, z0: number, z1: number): boolean {
  return x > x0 && x < x1 && z > z0 && z < z1;
}

function groundMaterial(x: number, z: number): Material {
  if (Math.abs(x) > 166 || Math.abs(z) > 166) return 'grass';
  if (inside(x, z, -50, 50, -45, 45)) return 'pavers';
  if (inside(x, z, -170, -65, -60, 42)) return 'dirt';
  if (inside(x, z, -60, 60, -170, -80)) return 'concrete';
  if (inside(x, z, 65, 166, -166, 60)) return 'concrete';
  if (Math.abs(x) > 50 && Math.abs(x) < 64 && Math.abs(z) < 45) return 'grass';
  if (Math.abs(z) > 45 && Math.abs(z) < 56 && Math.abs(x) < 50) return 'grass';
  // Asphalt paths cross the park; everything else is warm poured concrete.
  if (Math.abs(x) < 6 || Math.abs(z) < 6) return 'asphalt';
  return 'concrete';
}

function kickerRadius(height: number, angleDeg: number): number {
  return height / (1 - Math.cos((angleDeg * Math.PI) / 180));
}

function kicker(
  b: CourseBuilder,
  f: Frame,
  width: number,
  height: number,
  angleDeg: number,
  mat: Material = 'ramp',
  sideMat: Material = 'rampSide',
): number {
  const info = quarterPipe(b, f, {
    width,
    radius: kickerRadius(height, angleDeg),
    angle: angleDeg,
    deck: 0,
    back: true,
    coping: mat === 'ramp',
    mat,
    sideMat,
    map: mat === 'dirt' ? 'dirt' : 'ramp',
  });
  return info.lipZ;
}

/** Kicker, flat table, and landing ramp in a line rising toward local -z. */
function tabletop(
  b: CourseBuilder,
  f: Frame,
  width: number,
  height: number,
  angleDeg: number,
  table: number,
  landAngleDeg: number,
): void {
  const info = quarterPipe(b, f, {
    width,
    radius: kickerRadius(height, angleDeg),
    angle: angleDeg,
    deck: 0,
    back: false,
    coping: false,
    mat: 'dirt',
    sideMat: 'dirt',
    map: 'dirt',
  });
  box(b, f, 0, 0, info.lipZ - table / 2, width, height, table, {
    top: 'dirt',
    side: 'dirt',
    map: 'dirt',
    skip: ['pz', 'nz'],
  });
  const landLength = height / Math.tan((landAngleDeg * Math.PI) / 180);
  wedge(b, f.child(0, 0, info.lipZ - table - landLength, Math.PI), {
    width,
    length: landLength,
    height,
    back: false,
    mat: 'dirt',
    sideMat: 'dirt',
    map: 'dirt',
  });
}

/** An arched rail from `a` to `b` rising `arch` metres at its middle. */
function archRail(b: CourseBuilder, a: V3, c: V3, arch: number, mat: Material): void {
  const pts: V3[] = [];
  const segments = 16;
  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    pts.push([
      a[0] + (c[0] - a[0]) * t,
      a[1] + (c[1] - a[1]) * t + arch * 4 * t * (1 - t),
      a[2] + (c[2] - a[2]) * t,
    ]);
  }
  railWithPosts(b, pts, mat, undefined, 0.05, 8);
}

function container(b: CourseBuilder, f: Frame, level: number, mat: Material): void {
  box(b, f, 0, level * 2.6, 0, 12.2, 2.6, 2.44, {
    top: mat,
    side: mat,
    rails: 'long',
    bottom: level > 0,
  });
}

function ledge(b: CourseBuilder, f: Frame, length: number, height = 0.55, depth = 0.9): void {
  box(b, f, 0, 0, 0, length, height, depth, { top: 'granite', side: 'granite', rails: 'long' });
}

/** Four stepped rows rising toward local -z, spectators facing local +z. */
function bleachers(b: CourseBuilder, f: Frame, width: number): void {
  for (let row = 0; row < 4; row += 1) {
    box(b, f, 0, 0, 2.25 - row * 1.5, width, 0.6 * (row + 1), 1.5, {
      top: 'wood',
      side: 'granite',
      map: 'block',
      skip: row < 3 ? ['nz'] : [],
    });
  }
}

function lamp(decor: Decor[], x: number, z: number, yaw = 0): void {
  decor.push({ kind: 'lamp', p: [x, 0, z], yaw, scale: 1 });
}

function buildPlaza(b: CourseBuilder): void {
  const origin = new Frame(0, 0, 0);
  // Central stage with stairs, hubbas and banks.
  box(b, origin, 0, 0, 0, 28, 1.6, 16, { top: 'pavers', side: 'granite' });
  stairs(b, new Frame(0, 0, 8), { width: 8, steps: 6, rise: 1.6 / 6, run: 0.55 });
  for (const sx of [-1, 1]) {
    hubba(b, new Frame(0, 0, 8), sx * 4.5, {
      width: 0.9,
      length: 3.3,
      topHigh: 2.1,
      topLow: 0.5,
    });
    wedge(b, new Frame(sx * 9.5, 0, 12, RISE.north), {
      width: 9,
      length: 4,
      height: 1.6,
      back: false,
      mat: 'concrete',
      sideMat: 'granite',
    });
  }
  wedge(b, new Frame(0, 0, -13, RISE.south), {
    width: 28,
    length: 5,
    height: 1.6,
    back: false,
    mat: 'concrete',
    sideMat: 'granite',
  });
  const stageRadius = 2.2;
  const stageAngle = (Math.acos(1 - 1.6 / stageRadius) * 180) / Math.PI;
  const lip = stageRadius * Math.sin((stageAngle * Math.PI) / 180);
  for (const sx of [-1, 1]) {
    quarterPipe(b, new Frame(sx * (14 + lip), 0, 0, sx < 0 ? RISE.east : RISE.west), {
      width: 16,
      radius: stageRadius,
      angle: stageAngle,
      deck: 0,
      back: false,
      mat: 'concrete',
      sideMat: 'granite',
    });
  }

  // Ledges, pads and rails around the stage.
  ledge(b, new Frame(-30, 0, 20), 10);
  ledge(b, new Frame(30, 0, 20), 10);
  ledge(b, new Frame(-33, 0, -20, Math.PI / 2), 10);
  ledge(b, new Frame(33, 0, -20, Math.PI / 2), 10);
  box(b, origin, 0, 0, -30, 8, 0.3, 3.5, { top: 'concrete', side: 'granite', rails: 'all' });
  flatRail(b, [-24, 0.6, 31], [-12, 0.6, 31], 'railYellow');
  flatRail(b, [12, 0.6, 31], [24, 0.6, 31], 'railYellow');
  archRail(b, [-8, 0.5, 38], [8, 0.5, 38], 0.9, 'railRed');
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      box(b, new Frame(sx * 42, 0, sz * 37), 0, 0, 0, 6, 0.7, 6, {
        top: 'grass',
        side: 'granite',
        rails: 'all',
      });
      b.decor.push({ kind: 'tree', p: [sx * 42, 0.7, sz * 37], yaw: sx * sz, scale: 1.2 });
      b.decor.push({ kind: 'floodlight', p: [sx * 49, 0, sz * 44], yaw: 0, scale: 1 });
    }
  }
  // Neon gantry over the north approach.
  for (const sx of [-1, 1])
    box(b, origin, sx * 12, 0, -44, 0.5, 6.5, 0.5, { top: 'metal', side: 'metal', map: null });
  b.decor.push({ kind: 'sign', p: [0, 6.6, -44], yaw: 0, scale: 1, w: 24, h: 4, text: 'THRASHER' });
  for (let x = -40; x <= 40; x += 20) {
    lamp(b.decor, x, -47);
    lamp(b.decor, x, 47, Math.PI);
  }
}

/** Filler lines in the gaps between the main zones so no stretch of the park is empty. */
function buildConnectors(b: CourseBuilder): void {
  // Pyramid between the plaza and the bowls.
  const pyramid = new Frame(0, 0, -66);
  box(b, pyramid, 0, 0, 0, 7, 1.1, 7, { top: 'concrete', side: 'concrete', rails: 'all' });
  const bank = {
    width: 7,
    length: 3.2,
    height: 1.1,
    back: false,
    mat: 'concrete',
    sideMat: 'concrete',
  } as const;
  wedge(b, new Frame(0, 0, -62.5 + 3.2, RISE.north), bank);
  wedge(b, new Frame(0, 0, -69.5 - 3.2, RISE.south), bank);
  wedge(b, new Frame(-3.5 - 3.2, 0, -66, RISE.east), bank);
  wedge(b, new Frame(3.5 + 3.2, 0, -66, RISE.west), bank);
  // Ledge line between the plaza and the canal.
  for (const x of [-36, -12, 12, 36]) ledge(b, new Frame(x, 0, 70), 9, 0.5, 0.8);
  flatRail(b, [-24, 0.55, 78], [24, 0.55, 78], 'railRed');
  // East gap: kicker to ledge, and a long curb run.
  kicker(b, new Frame(76, 0, -14, RISE.north), 4, 0.9, 26);
  ledge(b, new Frame(76, 0, -26), 3, 0.9, 6);
  box(b, new Frame(82, 0, 24, Math.PI / 2), 0, 0, 0, 30, 0.35, 0.6, {
    top: 'curb',
    side: 'curb',
    rails: 'long',
  });
  kicker(b, new Frame(76, 0, 32, RISE.south), 4, 0.9, 26);
  // West gap next to the dirt: a hip made of two banks.
  wedge(b, new Frame(-56, 0, -70, RISE.north), {
    width: 10,
    length: 4,
    height: 1.3,
    back: false,
    mat: 'concrete',
    sideMat: 'concrete',
  });
  wedge(b, new Frame(-56, 0, -78, RISE.south), {
    width: 10,
    length: 4,
    height: 1.3,
    back: false,
    mat: 'concrete',
    sideMat: 'concrete',
  });
  for (const [x, z] of [
    [-70, 45],
    [70, -45],
    [-70, -45],
    [70, 48],
  ] as const) {
    lamp(b.decor, x, z);
  }
}

function buildBowls(b: CourseBuilder): void {
  sunkenBowl(b, {
    cx: -24,
    cz: -112,
    hx: 9,
    hz: 4.5,
    corner: 4.5,
    radius: 3.3,
    depth: 4.3,
    tileBand: 0.55,
    plot: PLOTS.mainPool,
  });
  sunkenBowl(b, {
    cx: 25,
    cz: -110,
    hx: 0,
    hz: 0,
    corner: 4.5,
    radius: 2.9,
    depth: 3.3,
    tileBand: 0.45,
    plot: PLOTS.roundBowl,
  });
  flatRail(b, [5, 0.55, -126], [5, 0.55, -98], 'railYellow');
  ledge(b, new Frame(-24, 0, -84), 9, 0.5, 0.8);
  ledge(b, new Frame(25, 0, -92), 7, 0.5, 0.8);
  bleachers(b, new Frame(-5, 0, -140), 50);
  b.decor.push({ kind: 'floodlight', p: [-52, 0, -86], yaw: 0, scale: 1.1 });
  b.decor.push({ kind: 'floodlight', p: [48, 0, -128], yaw: 0, scale: 1.1 });
  b.decor.push({
    kind: 'graffiti',
    p: [-5, 1.2, -143.05],
    yaw: Math.PI,
    scale: 1,
    w: 44,
    h: 2.2,
    seed: 3,
  });
  for (let x = -60; x <= 60; x += 30) lamp(b.decor, x, -78);
}

function buildVert(b: CourseBuilder): void {
  const halfpipe = { width: 18, radius: 3.6, vert: 1.1, deck: 3 };
  quarterPipe(b, new Frame(121, 0, 0, RISE.west), halfpipe);
  quarterPipe(b, new Frame(129, 0, 0, RISE.east), halfpipe);
  const deckHeight = halfpipe.radius + halfpipe.vert;
  wedge(b, new Frame(100.4, 0, -6, RISE.east), {
    width: 4,
    length: 14,
    height: deckHeight,
    back: false,
  });
  wedge(b, new Frame(149.6, 0, 6, RISE.west), {
    width: 4,
    length: 14,
    height: deckHeight,
    back: false,
  });
  b.decor.push({
    kind: 'graffiti',
    p: [114.35, 2.3, 0],
    yaw: -Math.PI / 2,
    scale: 1,
    w: 16,
    h: 4,
    seed: 7,
  });
  b.decor.push({
    kind: 'graffiti',
    p: [135.65, 2.3, 0],
    yaw: Math.PI / 2,
    scale: 1,
    w: 16,
    h: 4,
    seed: 11,
  });

  const mini = { width: 12, radius: 2.6, angle: 75, deck: 2 };
  quarterPipe(b, new Frame(123, 0, 42, RISE.west), mini);
  quarterPipe(b, new Frame(127, 0, 42, RISE.east), mini);
  flatRail(b, [100, 0.6, 18], [150, 0.6, 18], 'railRed');
  ledge(b, new Frame(96, 0, -30, Math.PI / 2), 12);
  b.decor.push({ kind: 'floodlight', p: [106, 0, -16], yaw: 0, scale: 1.2 });
  b.decor.push({ kind: 'floodlight', p: [144, 0, 16], yaw: 0, scale: 1.2 });
  bleachers(b, new Frame(125, 0, -36), 22);
}

function buildSpine(b: CourseBuilder): void {
  const spine = { width: 16, radius: 3.2, angle: 82, deck: 0, back: false };
  const lip = 3.2 * Math.sin((82 * Math.PI) / 180);
  quarterPipe(b, new Frame(118, 0, -118 + lip, RISE.north), spine);
  quarterPipe(b, new Frame(118, 0, -118 - lip, RISE.south), { ...spine, coping: false });
  const big = { width: 20, radius: 3.5, vert: 0.6, deck: 3 };
  quarterPipe(b, new Frame(118, 0, -140, RISE.north), big);
  quarterPipe(b, new Frame(118, 0, -96, RISE.south), big);
  kicker(b, new Frame(152, 0, -118, RISE.west), 5, 1.2, 28);
  ledge(b, new Frame(88, 0, -118, Math.PI / 2), 12, 1, 2);
  b.decor.push({
    kind: 'graffiti',
    p: [118, 2, -146.55],
    yaw: Math.PI,
    scale: 1,
    w: 18,
    h: 3.6,
    seed: 19,
  });
  b.decor.push({ kind: 'floodlight', p: [96, 0, -150], yaw: 0, scale: 1.1 });
  b.decor.push({ kind: 'floodlight', p: [150, 0, -88], yaw: 0, scale: 1.1 });
}

function buildDirt(b: CourseBuilder): void {
  // Line A heads south at x=-100, line B heads north at x=-140; berms turn riders around.
  for (const z of [-40, -14, 12]) tabletop(b, new Frame(-100, 0, z, RISE.south), 6, 1.8, 35, 4, 25);
  for (const z of [26, -2, -30]) tabletop(b, new Frame(-140, 0, z, RISE.north), 6, 2.4, 38, 6, 28);
  const berm = {
    width: 90,
    radius: 4,
    angle: 55,
    deck: 2,
    mat: 'dirt',
    sideMat: 'dirt',
    coping: false,
  } as const;
  quarterPipe(b, new Frame(-120, 0, 34, RISE.south), berm);
  quarterPipe(b, new Frame(-120, 0, -54, RISE.north), berm);
  for (let i = 0; i < 6; i += 1) {
    b.decor.push({ kind: 'barrel', p: [-80 + (i % 2) * 1.2, 0, -30 + i * 9], yaw: i, scale: 1 });
  }
  b.decor.push({ kind: 'floodlight', p: [-74, 0, -50], yaw: 0, scale: 1 });
  b.decor.push({ kind: 'floodlight', p: [-164, 0, 30], yaw: 0, scale: 1 });
}

function buildFunbox(b: CourseBuilder): void {
  box(b, new Frame(-115, 0, -118), 0, 0, 0, 22, 1.3, 14, { top: 'concrete', side: 'concrete' });
  const bank = {
    height: 1.3,
    length: 4,
    back: false,
    mat: 'concrete',
    sideMat: 'concrete',
  } as const;
  wedge(b, new Frame(-115, 0, -107, RISE.north), { ...bank, width: 22 });
  wedge(b, new Frame(-115, 0, -129, RISE.south), { ...bank, width: 22 });
  wedge(b, new Frame(-130, 0, -118, RISE.east), { ...bank, width: 14 });
  wedge(b, new Frame(-100, 0, -118, RISE.west), { ...bank, width: 14 });
  railWithPosts(
    b,
    [
      [-122, 1.85, -118],
      [-117, 1.85, -118],
      [-112, 1.85, -118],
      [-108, 1.85, -118],
    ],
    'railYellow',
    [
      [-122, 1.3, -118],
      [-117, 1.3, -118],
      [-112, 1.3, -118],
      [-108, 1.3, -118],
    ],
  );
  kicker(b, new Frame(-152, 0, -118, RISE.east), 5, 1, 25);
  kicker(b, new Frame(-78, 0, -118, RISE.west), 5, 1, 25);
  quarterPipe(b, new Frame(-115, 0, -150, RISE.north), {
    width: 30,
    radius: 3,
    vert: 0.4,
    deck: 2,
  });
  b.decor.push({
    kind: 'graffiti',
    p: [-115, 1.7, -155.05],
    yaw: Math.PI,
    scale: 1,
    w: 28,
    h: 3.2,
    seed: 23,
  });
  b.decor.push({ kind: 'floodlight', p: [-90, 0, -95], yaw: 0, scale: 1 });
}

function buildCanal(b: CourseBuilder): void {
  sunkenBowl(b, {
    cx: 0,
    cz: 110,
    hx: 118,
    hz: 0,
    corner: 3,
    radius: 5.5,
    depth: 3.6,
    plot: PLOTS.canal,
    floorMat: 'concrete',
    wallMat: 'concrete',
    deckMat: 'concrete',
  });
  // Bridge with its own banks and railings.
  box(b, new Frame(0, 0, 110), 0, 0.7, 0, 8, 0.5, 24, {
    top: 'concrete',
    side: 'concrete',
    bottom: true,
  });
  wedge(b, new Frame(0, 0, 93, RISE.south), {
    width: 8,
    length: 5,
    height: 1.2,
    back: false,
    mat: 'concrete',
    sideMat: 'concrete',
  });
  wedge(b, new Frame(0, 0, 127, RISE.north), {
    width: 8,
    length: 5,
    height: 1.2,
    back: false,
    mat: 'concrete',
    sideMat: 'concrete',
  });
  for (const sx of [-1, 1]) {
    const pts: V3[] = [];
    const bases: V3[] = [];
    for (let z = 98.2; z <= 121.9; z += 3.95) {
      pts.push([sx * 3.8, 2.1, z]);
      bases.push([sx * 3.8, 1.2, z]);
    }
    railWithPosts(b, pts, 'railYellow', bases);
  }
  for (const x of [-60, 60])
    railWithPosts(
      b,
      [
        [x, 0.9, 99],
        [x, 0.9, 121],
      ],
      'railRed',
    );
  ledge(b, new Frame(-40, 0, 95), 10, 0.5, 0.8);
  ledge(b, new Frame(40, 0, 95), 10, 0.5, 0.8);
  ledge(b, new Frame(-80, 0, 125), 10, 0.5, 0.8);
  ledge(b, new Frame(80, 0, 125), 10, 0.5, 0.8);
  for (let x = -110; x <= 110; x += 44) {
    lamp(b.decor, x, 97);
    lamp(b.decor, x + 22, 123, Math.PI);
  }
}

function buildSouthStreet(b: CourseBuilder): void {
  box(b, new Frame(0, 0, 160), 0, 0, 0, 100, 1.2, 12, {
    top: 'concrete',
    side: 'brick',
    rails: 'long',
  });
  for (const x of [-30, 0, 30]) {
    stairs(b, new Frame(x, 0, 154, Math.PI), { width: 6, steps: 4, rise: 0.3, run: 0.55 });
  }
  for (const [x, width] of [
    [-15, 8],
    [15, 8],
    [-43, 12],
    [43, 12],
  ] as const) {
    wedge(b, new Frame(x, 0, 150, RISE.south), {
      width,
      length: 4,
      height: 1.2,
      back: false,
      mat: 'concrete',
      sideMat: 'brick',
    });
  }
  b.decor.push({
    kind: 'graffiti',
    p: [0, 0.6, 166.05],
    yaw: 0,
    scale: 1,
    w: 90,
    h: 1.1,
    seed: 31,
  });
  kicker(b, new Frame(-80, 0, 140, RISE.north), 5, 1, 24);
  kicker(b, new Frame(80, 0, 140, RISE.north), 5, 1, 24);
  box(b, new Frame(-60, 0, 140), 0, 0, 0, 8, 0.3, 3.5, {
    top: 'concrete',
    side: 'granite',
    rails: 'all',
  });
  box(b, new Frame(60, 0, 140), 0, 0, 0, 8, 0.3, 3.5, {
    top: 'concrete',
    side: 'granite',
    rails: 'all',
  });
  b.decor.push({ kind: 'floodlight', p: [-56, 0, 170], yaw: 0, scale: 1 });
  b.decor.push({ kind: 'floodlight', p: [56, 0, 170], yaw: 0, scale: 1 });
}

function buildYard(b: CourseBuilder): void {
  container(b, new Frame(-80, 0, 60), 0, 'containerRed');
  container(b, new Frame(-80, 0, 70), 0, 'containerBlue');
  wedge(b, new Frame(-65.9, 0, 60, RISE.west), {
    width: 2.44,
    length: 8,
    height: 2.6,
    back: false,
  });
  wedge(b, new Frame(-94.1, 0, 70, RISE.east), {
    width: 2.44,
    length: 8,
    height: 2.6,
    back: false,
  });
  container(b, new Frame(-110, 0, 65, Math.PI / 2), 0, 'containerOrange');
  container(b, new Frame(-110, 0, 65, Math.PI / 2), 1, 'containerRed');
  wedge(b, new Frame(-94.78, 0, 65, RISE.west), {
    width: 2.44,
    length: 14,
    height: 5.2,
    back: false,
  });
  container(b, new Frame(-140, 0, 58, 0.3), 0, 'containerBlue');
  container(b, new Frame(-142, 0, 80, -0.2), 0, 'containerOrange');
  kicker(b, new Frame(-128, 0, 69, RISE.west), 4, 1.1, 28);
  quarterPipe(b, new Frame(-110, 0, 86, RISE.south), { width: 30, radius: 3, vert: 0.5, deck: 2 });
  ledge(b, new Frame(-155, 0, 130, Math.PI / 2), 14, 0.6, 1);
  kicker(b, new Frame(-155, 0, 152, RISE.north), 5, 1.1, 26);
  flatRail(b, [-120, 0.7, 130], [-100, 0.7, 138], 'railRed');
  for (let i = 0; i < 8; i += 1) {
    b.decor.push({ kind: 'cone', p: [-150 + i * 3, 0, 100], yaw: 0, scale: 1 });
  }
  b.decor.push({ kind: 'floodlight', p: [-66, 0, 48], yaw: 0, scale: 1 });
  b.decor.push({ kind: 'floodlight', p: [-160, 0, 92], yaw: 0, scale: 1 });
}

function buildLaunch(b: CourseBuilder): void {
  const lipX = 80 - kicker(b, new Frame(80, 0, 70, RISE.east), 8, 2.6, 32);
  const tableStart = lipX + 8;
  box(b, new Frame(tableStart + 2, 0, 70), 0, 0, 0, 4, 2.6, 8, {
    top: 'deck',
    side: 'rampSide',
    skip: ['px'],
  });
  const landing = 2.6 / Math.tan((28 * Math.PI) / 180);
  wedge(b, new Frame(tableStart + 4 + landing, 0, 70, RISE.west), {
    width: 8,
    length: landing,
    height: 2.6,
    back: false,
  });
  b.decor.push({
    kind: 'graffiti',
    p: [tableStart - 0.05, 1.3, 70],
    yaw: -Math.PI / 2,
    scale: 1,
    w: 7.6,
    h: 2.4,
    seed: 41,
  });

  flatRail(b, [115, 0.6, 50], [160, 0.6, 50], 'railYellow');
  const curve: V3[] = [];
  for (let i = 0; i <= 24; i += 1) {
    const z = 60 + (i / 24) * 26;
    curve.push([130 + 4 * Math.sin(((z - 60) / 26) * Math.PI * 2), 0.7, z]);
  }
  railWithPosts(b, curve, 'railRed', undefined, 0.045, 4);
  archRail(b, [150, 0.5, 58], [150, 0.5, 74], 1.1, 'railYellow');
  box(b, new Frame(150, 0, 86), 0, 0, 0, 8, 1.5, 6, { top: 'concrete', side: 'brick' });
  stairs(b, new Frame(150, 0, 83, Math.PI), { width: 5, steps: 5, rise: 0.3, run: 0.6 });
  wedge(b, new Frame(150, 0, 94, RISE.north), {
    width: 8,
    length: 5,
    height: 1.5,
    back: false,
    mat: 'concrete',
    sideMat: 'brick',
  });
  sunkenBowl(b, {
    cx: 155,
    cz: 140,
    hx: 0,
    hz: 0,
    corner: 3,
    radius: 2.2,
    depth: 2.5,
    tileBand: 0.3,
    plot: PLOTS.cornerBowl,
  });
  b.decor.push({ kind: 'floodlight', p: [112, 0, 88], yaw: 0, scale: 1 });
  b.decor.push({ kind: 'floodlight', p: [166, 0, 110], yaw: 0, scale: 1 });
}

function buildPerimeter(b: CourseBuilder): void {
  const half = COURSE_HALF_EXTENT;
  const bank = {
    radius: 3,
    angle: 70,
    deck: 1.5,
    flags: SURFACE.contain,
    mat: 'concrete',
    sideMat: 'concrete',
    deckMat: 'concrete',
    coping: false,
    map: 'wall',
  } as const;
  const depth = 3 * Math.sin((70 * Math.PI) / 180) + 1.5;
  const height = 3 * (1 - Math.cos((70 * Math.PI) / 180));
  const span = (half - depth) * 2;
  quarterPipe(b, new Frame(0, 0, -half + depth, RISE.north), { ...bank, width: span });
  quarterPipe(b, new Frame(0, 0, half - depth, RISE.south), { ...bank, width: span });
  quarterPipe(b, new Frame(half - depth, 0, 0, RISE.east), { ...bank, width: span });
  quarterPipe(b, new Frame(-half + depth, 0, 0, RISE.west), { ...bank, width: span });
  const origin = new Frame(0, 0, 0);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      box(b, origin, sx * (half - depth / 2), 0, sz * (half - depth / 2), depth, height, depth, {
        top: 'concrete',
        side: 'concrete',
        map: 'wall',
      });
    }
  }
  // Fence: solid collision walls with render-side chain link.
  const fenceHeight = 6;
  const walls: [number, number, number, number][] = [
    [0, -half - 0.2, half * 2 + 0.8, 0.4],
    [0, half + 0.2, half * 2 + 0.8, 0.4],
    [-half - 0.2, 0, 0.4, half * 2 + 0.8],
    [half + 0.2, 0, 0.4, half * 2 + 0.8],
  ];
  for (const [x, z, w, d] of walls) {
    box(b, origin, x, 0, z, w, fenceHeight, d, {
      top: 'metal',
      side: 'paintedWall',
      render: false,
      map: null,
    });
  }
  for (let t = -half; t <= half; t += 12) {
    for (const [x, z] of [
      [t, -half],
      [t, half],
      [-half, t],
      [half, t],
    ] as const) {
      post(b, [x, 0, z], fenceHeight, 0.06, 'metal');
    }
  }
  for (const [a, c] of [
    [
      [-half, fenceHeight, -half],
      [half, fenceHeight, -half],
    ],
    [
      [-half, fenceHeight, half],
      [half, fenceHeight, half],
    ],
    [
      [-half, fenceHeight, -half],
      [-half, fenceHeight, half],
    ],
    [
      [half, fenceHeight, -half],
      [half, fenceHeight, half],
    ],
  ] as [V3, V3][]) {
    tube(b, [a, c], 0.05, 'metal');
  }
  for (let t = -150; t <= 150; t += 30) {
    b.decor.push({
      kind: 'palm',
      p: [t + 8, height, -half + 1.2],
      yaw: t,
      scale: 1 + ((t / 30) % 2) * 0.1,
    });
    b.decor.push({ kind: 'palm', p: [t - 8, height, half - 1.2], yaw: t * 0.7, scale: 1.05 });
    b.decor.push({ kind: 'palm', p: [-half + 1.2, height, t + 4], yaw: t * 1.3, scale: 0.95 });
    b.decor.push({ kind: 'palm', p: [half - 1.2, height, t - 4], yaw: t * 0.4, scale: 1.1 });
  }
}

/** Deterministic tree clusters on the grass strips between zones. */
function scatterGreenery(b: CourseBuilder): void {
  let seed = 1337;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const spots: [number, number][] = [];
  for (let i = 0; i < 26; i += 1) {
    const side = i % 2 === 0 ? -1 : 1;
    spots.push([side * (53 + random() * 9), -40 + random() * 80]);
  }
  for (let i = 0; i < 20; i += 1) {
    const side = i % 2 === 0 ? -1 : 1;
    spots.push([-45 + random() * 90, side * (47 + random() * 7)]);
  }
  for (let i = 0; i < 36; i += 1) {
    const edge = i % 4;
    const t = -160 + random() * 320;
    const inset = 167 + random() * 5;
    spots.push(
      edge === 0 ? [t, -inset] : edge === 1 ? [t, inset] : edge === 2 ? [-inset, t] : [inset, t],
    );
  }
  for (const [x, z] of spots) {
    // Keep clear of spawns and anything already standing there.
    if (COURSE_SPAWNS.some((s) => Math.hypot(s.p[0] - x, s.p[2] - z) < 6)) continue;
    if (b.decor.some((d) => Math.hypot(d.p[0] - x, d.p[2] - z) < 4)) continue;
    b.decor.push({
      kind: 'tree',
      p: [x, 0, z],
      yaw: random() * Math.PI * 2,
      scale: 0.9 + random() * 0.6,
    });
  }
}

/** Builds the full Thrasher park: render chunks, collision, rails, map shapes and decor. */
export function buildCourse(): Course {
  const b = new CourseBuilder();
  ground(b, COURSE_HALF_EXTENT, 10, Object.values(PLOTS), groundMaterial);
  buildPlaza(b);
  buildConnectors(b);
  buildBowls(b);
  buildVert(b);
  buildSpine(b);
  buildDirt(b);
  buildFunbox(b);
  buildCanal(b);
  buildSouthStreet(b);
  buildYard(b);
  buildLaunch(b);
  buildPerimeter(b);
  scatterGreenery(b);
  // Painted ground art (render-only decals).
  const paints: [number, number, number, number, number][] = [
    [0, 30, 0, 14, 1],
    [-30, -32, 0.4, 10, 2],
    [30, 32, -0.3, 10, 3],
    [-110, -100, 0, 12, 4],
    [118, -104, 0, 10, 5],
    [0, 88, 0, 16, 6],
    [-96, 52, 0.6, 9, 7],
    [100, 40, 0, 12, 8],
    [0, -60, 0, 18, 9],
    [60, 0, 1.2, 12, 10],
    [-60, 0, -1.2, 12, 11],
  ];
  for (const [x, z, yaw, size, seed] of paints) {
    b.decor.push({ kind: 'paint', p: [x, 0.012, z], yaw, scale: 1, w: size, h: size, seed });
  }
  // Solid bases for lamps, floodlight towers and palms.
  for (const d of [...b.decor]) {
    const size =
      d.kind === 'floodlight'
        ? 1
        : d.kind === 'lamp'
          ? 0.3
          : d.kind === 'palm' || d.kind === 'tree'
            ? 0.5
            : 0;
    if (size === 0) continue;
    box(b, new Frame(d.p[0], d.p[1], d.p[2]), 0, 0, 0, size, 5, size, {
      top: 'metal',
      side: 'metal',
      render: false,
      map: null,
    });
  }
  b.zones.push(
    { name: 'Plaza', x: 0, z: 0 },
    { name: 'Bowls', x: 0, z: -110 },
    { name: 'Vert', x: 125, z: 10 },
    { name: 'Spine', x: 118, z: -128 },
    { name: 'Dirt Line', x: -120, z: -10 },
    { name: 'Funbox', x: -115, z: -130 },
    { name: 'Canal', x: 0, z: 110 },
    { name: 'Container Yard', x: -112, z: 72 },
    { name: 'Launch', x: 100, z: 70 },
    { name: 'South Street', x: 0, z: 150 },
  );
  return {
    halfExtent: COURSE_HALF_EXTENT,
    render: b.render,
    collision: b.collision(),
    rails: b.rails,
    shapes: b.shapes,
    decor: b.decor,
    zones: b.zones,
    spawns: [...COURSE_SPAWNS],
  };
}
